<template>
  <div class="superadmin-layout min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
    <!-- Sidebar -->
    <aside
      :class="[
        'fixed inset-y-0 left-0 z-50 w-72 bg-gradient-to-b from-indigo-900 via-purple-900 to-purple-800 transform transition-transform duration-300 shadow-2xl',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        'lg:translate-x-0'
      ]"
    >
      <div class="flex flex-col h-full">
        <!-- Logo & Branding -->
        <div class="flex items-center justify-between h-20 px-6 bg-indigo-950 border-b border-indigo-800">
          <router-link to="/superadmin/dashboard" class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <span class="text-white font-bold text-xl">SuperAdmin</span>
              <p class="text-indigo-300 text-xs">System Control</p>
            </div>
          </router-link>
          <button
            @click="sidebarOpen = false"
            class="lg:hidden text-indigo-300 hover:text-white"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Quick Tenant Switcher -->
        <div class="px-4 py-4 border-b border-indigo-800">
          <div class="bg-indigo-950/50 rounded-lg p-3">
            <label class="text-indigo-300 text-xs font-medium uppercase tracking-wide mb-2 block">Active Tenant</label>
            <select
              v-model="selectedTenantId"
              @change="switchTenant"
              class="w-full bg-indigo-900 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Tenants</option>
              <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
                {{ tenant.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 px-4 py-6 overflow-y-auto scrollbar-thin scrollbar-thumb-indigo-700 scrollbar-track-indigo-950">
          <ul class="space-y-2">
            <!-- Dashboard -->
            <li>
              <router-link
                to="/superadmin/dashboard"
                class="flex items-center px-4 py-3 text-indigo-200 rounded-xl hover:bg-indigo-800/50 hover:text-white transition-all group"
                active-class="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg"
              >
                <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Dashboard
              </router-link>
            </li>

            <!-- Tenants Section -->
            <li class="pt-4">
              <p class="text-indigo-400 text-xs font-semibold uppercase tracking-wider px-4 mb-2">Tenant Management</p>
            </li>
            <li>
              <router-link
                to="/superadmin/tenants"
                class="flex items-center px-4 py-3 text-indigo-200 rounded-xl hover:bg-indigo-800/50 hover:text-white transition-all"
                active-class="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg"
              >
                <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                All Tenants
                <span class="ml-auto bg-purple-600 text-white text-xs px-2 py-1 rounded-full">{{ tenants.length }}</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/superadmin/tenants/new"
                class="flex items-center px-4 py-3 text-indigo-200 rounded-xl hover:bg-indigo-800/50 hover:text-white transition-all"
                active-class="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg"
              >
                <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
                Create Tenant
              </router-link>
            </li>

            <!-- Financial Section -->
            <li class="pt-4">
              <p class="text-indigo-400 text-xs font-semibold uppercase tracking-wider px-4 mb-2">Financial</p>
            </li>
            <li>
              <router-link
                to="/superadmin/billing"
                class="flex items-center px-4 py-3 text-indigo-200 rounded-xl hover:bg-indigo-800/50 hover:text-white transition-all"
                active-class="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg"
              >
                <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Billing
              </router-link>
            </li>

            <!-- Analytics & Reports -->
            <li class="pt-4">
              <p class="text-indigo-400 text-xs font-semibold uppercase tracking-wider px-4 mb-2">Analytics</p>
            </li>
            <li>
              <router-link
                to="/superadmin/analytics"
                class="flex items-center px-4 py-3 text-indigo-200 rounded-xl hover:bg-indigo-800/50 hover:text-white transition-all"
                active-class="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg"
              >
                <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Analytics
              </router-link>
            </li>

            <!-- System Section -->
            <li class="pt-4">
              <p class="text-indigo-400 text-xs font-semibold uppercase tracking-wider px-4 mb-2">System</p>
            </li>
            <li>
              <router-link
                to="/superadmin/settings"
                class="flex items-center px-4 py-3 text-indigo-200 rounded-xl hover:bg-indigo-800/50 hover:text-white transition-all"
                active-class="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg"
              >
                <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </router-link>
            </li>
          </ul>
        </nav>

        <!-- User Info -->
        <div class="p-4 border-t border-indigo-800 bg-indigo-950">
          <div class="flex items-center">
            <div class="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
              <span class="text-white font-bold text-sm">{{ userInitials }}</span>
            </div>
            <div class="ml-3 flex-1">
              <p class="text-white text-sm font-medium">{{ userName }}</p>
              <p class="text-indigo-300 text-xs flex items-center">
                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Super Admin
              </p>
            </div>
            <button
              @click="logout"
              class="text-indigo-300 hover:text-white transition-colors"
              title="Logout"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </aside>

    <!-- Overlay for mobile -->
    <div
      v-if="sidebarOpen"
      @click="sidebarOpen = false"
      class="fixed inset-0 bg-black bg-opacity-60 z-40 lg:hidden backdrop-blur-sm"
    ></div>

    <!-- Main content -->
    <div class="lg:pl-72">
      <!-- Top bar -->
      <header class="bg-white/80 backdrop-blur-lg shadow-sm sticky top-0 z-30 border-b border-gray-200">
        <div class="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          <div class="flex items-center space-x-4">
            <button
              @click="sidebarOpen = !sidebarOpen"
              class="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div>
              <h1 class="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                {{ pageTitle }}
              </h1>
              <p class="text-xs text-gray-500">{{ pageSubtitle }}</p>
            </div>
          </div>

          <div class="flex items-center space-x-3">
            <!-- System Health Indicator -->
            <div class="hidden sm:flex items-center space-x-2 px-3 py-1.5 bg-green-50 rounded-lg">
              <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span class="text-xs font-medium text-green-700">System Healthy</span>
            </div>

            <!-- Notifications -->
            <button class="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span class="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"></span>
            </button>

            <!-- Quick Actions -->
            <button
              @click="showQuickActions = !showQuickActions"
              class="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <!-- System-wide notification banner -->
      <div v-if="systemNotification" class="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-6 py-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <span class="font-medium">{{ systemNotification }}</span>
          </div>
          <button @click="systemNotification = null" class="text-white hover:text-gray-200">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Page content -->
      <main class="p-4 sm:p-6 lg:p-8">
        <router-view />
      </main>
    </div>

    <!-- Quick Actions Modal -->
    <div v-if="showQuickActions" class="fixed inset-0 z-50 overflow-y-auto" @click="showQuickActions = false">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-black opacity-50"></div>
        <div class="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" @click.stop>
          <h3 class="text-lg font-bold mb-4 text-gray-900">Quick Actions</h3>
          <div class="space-y-2">
            <router-link to="/superadmin/tenants/new" class="block p-3 hover:bg-gray-50 rounded-lg transition-colors">
              <div class="font-medium text-gray-900">Create New Tenant</div>
              <div class="text-sm text-gray-500">Set up a new tenant account</div>
            </router-link>
            <router-link to="/superadmin/analytics" class="block p-3 hover:bg-gray-50 rounded-lg transition-colors">
              <div class="font-medium text-gray-900">View Analytics</div>
              <div class="text-sm text-gray-500">System-wide performance metrics</div>
            </router-link>
            <router-link to="/superadmin/settings" class="block p-3 hover:bg-gray-50 rounded-lg transition-colors">
              <div class="font-medium text-gray-900">System Settings</div>
              <div class="text-sm text-gray-500">Configure global settings</div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTenantStore } from '@/stores/tenant'
import logger from '@/utils/logger'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const tenantStore = useTenantStore()

const sidebarOpen = ref(false)
const showQuickActions = ref(false)
const systemNotification = ref(null)
const selectedTenantId = ref('')
const tenants = ref([])

onMounted(async () => {
  await tenantStore.fetchAllTenants()
  tenants.value = tenantStore.allTenants
  logger.info('SUPERADMIN_LAYOUT', 'SuperAdmin layout initialized')
})

const userName = computed(() => authStore.user?.name || 'Super Admin')
const userInitials = computed(() => {
  const name = authStore.user?.name || 'SA'
  return name.split(' ').map(n => n[0]).join('').toUpperCase()
})
const pageTitle = computed(() => route.meta.title || 'SuperAdmin')
const pageSubtitle = computed(() => route.meta.subtitle || 'System Management')

const switchTenant = async () => {
  if (selectedTenantId.value) {
    await tenantStore.switchTenant(selectedTenantId.value)
    systemNotification.value = `Switched to tenant: ${tenantStore.currentTenant?.name}`
  }
}

const logout = async () => {
  await authStore.logout()
  router.push('/auth/login')
}
</script>

<style scoped>
.router-link-active {
  @apply bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>